// src/pages/BecomeStylistPage.tsx
//
// Public "Become a Stylist" page — the professional funnel next to guest client
// discovery (Find a Stylist). Learning about LookBel needs no account; "Join
// LookBel" hands off to the existing stylist registration + onboarding
// (/register?userType=stylist), which is where an account is created.
// Copy mirrors the homepage "For Stylists" section.
import React from 'react';
import { Link } from 'react-router-dom';
import MainNavigation from '../components/common/MainNavigation';
import SiteFooter from '../components/common/SiteFooter';
import TeslaButton from '../components/common/TeslaButton';
import { useAppSelector } from '../hooks/useRedux';
import '../styles/BecomeStylistPage.css';

export const STYLIST_REGISTER_PATH = '/register?userType=stylist';

const BENEFITS = [
  {
    title: 'Free to start',
    text: 'No upfront costs or monthly fees. LookBel is commission-based — you pay only when you earn.',
  },
  {
    title: 'Get discovered',
    text: 'Clients near you find your profile, services, prices, portfolio and reviews — and book.',
  },
  {
    title: 'Smart booking',
    text: 'Manage availability, appointments, policies and client messages in one place.',
  },
  {
    title: 'Secure payments',
    text: 'Clients save a card when they book; you get paid out through Stripe after the service.',
  },
];

const BecomeStylistPage: React.FC = () => {
  const { token, user } = useAppSelector((state) => state.auth);
  const userType = user?.userType || user?.role;
  const signedIn = Boolean(token && user);

  return (
    <div className="become-stylist">
      <MainNavigation variant="solid" />
      <main className="become-stylist__content">
        <section className="become-stylist__hero">
          <p className="become-stylist__eyebrow">For beauty professionals</p>
          <h1 className="become-stylist__title">Grow your business with LookBel</h1>
          <p className="become-stylist__lead">
            Join LookBel to reach clients looking for your services and manage bookings,
            payments and clients in one place.
          </p>

          {signedIn && userType === 'stylist' ? (
            <Link to="/stylist/dashboard" className="become-stylist__cta-link">
              <TeslaButton variant="primary" size="lg">Go to your dashboard</TeslaButton>
            </Link>
          ) : signedIn ? (
            <p className="become-stylist__note" data-testid="become-stylist-client-note">
              You're signed in with a client account. Stylist accounts are separate — sign out
              and join LookBel as a stylist with a different email address.
            </p>
          ) : (
            <div className="become-stylist__actions">
              <Link to={STYLIST_REGISTER_PATH} className="become-stylist__cta-link">
                <TeslaButton variant="primary" size="lg">Join LookBel</TeslaButton>
              </Link>
              <p className="become-stylist__signin">
                Already have a stylist account? <Link to="/login">Sign in</Link>
              </p>
            </div>
          )}
        </section>

        <section className="become-stylist__benefits" aria-label="Why join LookBel">
          {BENEFITS.map((benefit) => (
            <div key={benefit.title} className="become-stylist__benefit">
              <h2>{benefit.title}</h2>
              <p>{benefit.text}</p>
            </div>
          ))}
        </section>

        <section className="become-stylist__steps">
          <h2>How joining works</h2>
          <ol>
            <li>Create your stylist account and accept the Service Provider Agreement.</li>
            <li>Verify your email and set up your profile, services and availability.</li>
            <li>Connect payouts, then start accepting bookings.</li>
          </ol>
          {!signedIn && (
            <Link to={STYLIST_REGISTER_PATH} className="become-stylist__cta-link">
              <TeslaButton variant="primary" size="md">Get Started</TeslaButton>
            </Link>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default BecomeStylistPage;
